// Discriminated-union state machine for the xlsx upload flow
// (ADR-0024). Three phases: pick → preview → commit. Reducer-only —
// the screen calls dispatch() in mutation callbacks, the type system
// enforces that you can't render commit-stage UI in pick state.

import type {
  AdminWorkbookCommitResponse,
  AdminWorkbookParseIssue,
  AdminWorkbookPreviewResponse,
} from "@bitacorapro/api-types";

export type UploadState =
  | { kind: "idle" }
  | { kind: "file-picked"; file: File; orgName: string; recipeSlug: string; rut: string }
  | { kind: "preview-pending"; file: File; orgName: string; recipeSlug: string; rut: string }
  | {
      kind: "preview-ready";
      file: File;
      orgName: string;
      recipeSlug: string;
      rut: string;
      preview: AdminWorkbookPreviewResponse;
    }
  | {
      kind: "preview-error";
      file: File;
      orgName: string;
      recipeSlug: string;
      rut: string;
      error: string;
      // ADR-0088 §Ingest follow-up. Structured per-row detail from
      // ApiError.details.issues, when the server sent it (a WORKBOOK_INVALID
      // 422). Absent for non-parse failures (network error, 500, an older
      // core-api that hasn't shipped the wrapper) — those fall back to
      // rendering `error` alone.
      issues?: AdminWorkbookParseIssue[] | undefined;
    }
  | {
      kind: "commit-pending";
      preview: AdminWorkbookPreviewResponse;
    }
  | {
      kind: "commit-done";
      result: AdminWorkbookCommitResponse;
    }
  | {
      kind: "commit-error";
      preview: AdminWorkbookPreviewResponse;
      error: string;
    };

// commit-done is terminal. The optional logos uploaded after the commit
// (company + one per brand) run in their own queue — see logoQueue.ts —
// so a logo that fails or is skipped can never move this machine back
// behind a created org.

export type UploadAction =
  | { type: "file-picked"; file: File }
  | { type: "set-meta"; field: "orgName" | "recipeSlug" | "rut"; value: string }
  | { type: "preview-start" }
  | { type: "preview-success"; preview: AdminWorkbookPreviewResponse }
  | { type: "preview-fail"; error: string; issues?: AdminWorkbookParseIssue[] | undefined }
  | { type: "commit-start" }
  | { type: "commit-success"; result: AdminWorkbookCommitResponse }
  | { type: "commit-fail"; error: string }
  | { type: "reset" };

function withMeta(
  state: UploadState,
  field: "orgName" | "recipeSlug" | "rut",
  value: string
): UploadState {
  // Editing metadata after a successful preview discards the preview
  // — the operator must re-run the dry-run on the new context.
  if (state.kind === "file-picked" || state.kind === "preview-error") {
    return { ...state, [field]: value };
  }
  if (state.kind === "preview-ready" || state.kind === "preview-pending") {
    return {
      kind: "file-picked",
      file: state.file,
      orgName: field === "orgName" ? value : state.orgName,
      recipeSlug: field === "recipeSlug" ? value : state.recipeSlug,
      rut: field === "rut" ? value : state.rut,
    };
  }
  return state;
}

export function uploadReducer(state: UploadState, action: UploadAction): UploadState {
  switch (action.type) {
    case "file-picked":
      return {
        kind: "file-picked",
        file: action.file,
        orgName: "",
        recipeSlug: "",
        rut: "",
      };
    case "set-meta":
      return withMeta(state, action.field, action.value);
    case "preview-start":
      if (state.kind !== "file-picked" && state.kind !== "preview-error") return state;
      return {
        kind: "preview-pending",
        file: state.file,
        orgName: state.orgName,
        recipeSlug: state.recipeSlug,
        rut: state.rut,
      };
    case "preview-success":
      if (state.kind !== "preview-pending") return state;
      return {
        kind: "preview-ready",
        file: state.file,
        orgName: state.orgName,
        recipeSlug: state.recipeSlug,
        rut: state.rut,
        preview: action.preview,
      };
    case "preview-fail":
      if (state.kind !== "preview-pending") return state;
      return {
        kind: "preview-error",
        file: state.file,
        orgName: state.orgName,
        recipeSlug: state.recipeSlug,
        rut: state.rut,
        error: action.error,
        issues: action.issues,
      };
    case "commit-start":
      if (state.kind !== "preview-ready") return state;
      return { kind: "commit-pending", preview: state.preview };
    case "commit-success":
      if (state.kind !== "commit-pending") return state;
      return { kind: "commit-done", result: action.result };
    case "commit-fail":
      if (state.kind !== "commit-pending") return state;
      return {
        kind: "commit-error",
        preview: state.preview,
        error: action.error,
      };
    case "reset":
      return { kind: "idle" };
    default:
      return state;
  }
}

export function canPreview(state: UploadState): boolean {
  return (
    (state.kind === "file-picked" || state.kind === "preview-error") &&
    state.orgName.trim().length > 0 &&
    state.recipeSlug.length > 0
  );
}

export function canCommit(state: UploadState): boolean {
  return state.kind === "preview-ready";
}
